import { Reader } from "./reader.ts";

/** How the caller's value of a register is found, relative to the CFA. */
export type Rule =
    | { kind: "undefined" }
    | { kind: "same" }
    | { kind: "offset"; offset: number }
    | { kind: "valOffset"; offset: number }
    | { kind: "register"; register: number }
    | { kind: "unsupported" };

/** The unwind rules in effect at one address. */
export interface FrameState {
    cfaRegister: number;
    cfaOffset: number;
    /** False when the CFA is a DWARF expression, which this reader does not evaluate. */
    cfaKnown: boolean;
    returnAddress: number;
    rules: Map<number, Rule>;
}

interface Cie {
    codeAlign: number;
    dataAlign: number;
    returnAddress: number;
    addressSize: number;
    instructions: Uint8Array;
}

interface Fde {
    start: number;
    end: number;
    cie: Cie;
    instructions: Uint8Array;
}

/**
 * The call frame information in .debug_frame: for any code address, where
 * the caller's registers were saved. This is what GDB unwinds a RISC-V stack
 * dump with.
 */
export class FrameTable {
    readonly #fdes: Fde[];

    constructor(section: Uint8Array) {
        const cies = new Map<number, Cie>();
        const fdes: Fde[] = [];
        const r = new Reader(section);
        while (r.offset + 4 <= section.length) {
            const offset = r.offset;
            const length = r.u32();
            if (length === 0) continue;
            if (length === 0xffffffff) throw new Error("64-bit DWARF is not supported");
            const end = r.offset + length;
            const id = r.u32();
            if (id === 0xffffffff) {
                const cie = readCie(r, end);
                if (cie !== undefined) cies.set(offset, cie);
            } else {
                const cie = cies.get(id);
                if (cie !== undefined) {
                    const start = r.sized(cie.addressSize);
                    const range = r.sized(cie.addressSize);
                    fdes.push({ start, end: start + range, cie, instructions: section.subarray(r.offset, end) });
                }
            }
            r.offset = end;
        }
        this.#fdes = fdes.sort((a, b) => a.start - b.start);
    }

    /** The rules at an address, or undefined if no FDE covers it. */
    stateAt(address: number): FrameState | undefined {
        const fde = this.#find(address);
        if (fde === undefined) return undefined;
        const cie = fde.cie;
        const state: FrameState = {
            cfaRegister: 0,
            cfaOffset: 0,
            cfaKnown: true,
            returnAddress: cie.returnAddress,
            rules: new Map(),
        };
        execute(cie.instructions, cie, state, undefined, Infinity, 0);
        const initial = new Map(state.rules);
        execute(fde.instructions, cie, state, initial, address, fde.start);
        return state;
    }

    #find(address: number): Fde | undefined {
        const fdes = this.#fdes;
        let low = 0;
        let high = fdes.length;
        while (low < high) {
            const mid = (low + high) >>> 1;
            if (fdes[mid]!.start <= address) low = mid + 1;
            else high = mid;
        }
        const fde = fdes[low - 1];
        return fde !== undefined && address < fde.end ? fde : undefined;
    }
}

function readCie(r: Reader, end: number): Cie | undefined {
    const version = r.u8();
    const augmentation = r.cstr();
    // Augmentations change the layout, and .debug_frame from clang has none.
    if (augmentation !== "") return undefined;
    let addressSize = 4;
    if (version >= 4) {
        addressSize = r.u8();
        r.u8();
    }
    const codeAlign = r.uleb();
    const dataAlign = r.sleb();
    const returnAddress = version === 1 ? r.u8() : r.uleb();
    return { codeAlign, dataAlign, returnAddress, addressSize, instructions: r.bytes.subarray(r.offset, end) };
}

/** Runs CFA instructions until the location passes `target`. */
function execute(
    instructions: Uint8Array,
    cie: Cie,
    state: FrameState,
    initial: Map<number, Rule> | undefined,
    target: number,
    start: number,
): void {
    const r = new Reader(instructions);
    const saved: { rules: Map<number, Rule>; cfaRegister: number; cfaOffset: number }[] = [];
    let location = start;
    const advance = (delta: number): boolean => {
        location += delta * cie.codeAlign;
        return location <= target;
    };
    const restore = (register: number) => {
        const rule = initial?.get(register);
        if (rule === undefined) state.rules.delete(register);
        else state.rules.set(register, rule);
    };
    while (!r.done) {
        const byte = r.u8();
        const high = byte & 0xc0;
        const low = byte & 0x3f;
        if (high === 0x40) {
            if (!advance(low)) return;
            continue;
        }
        if (high === 0x80) {
            state.rules.set(low, { kind: "offset", offset: r.uleb() * cie.dataAlign });
            continue;
        }
        if (high === 0xc0) {
            restore(low);
            continue;
        }
        switch (byte) {
            case 0x00: // nop
                break;
            case 0x01: // set_loc
                location = r.sized(cie.addressSize);
                if (location > target) return;
                break;
            case 0x02:
                if (!advance(r.u8())) return;
                break;
            case 0x03:
                if (!advance(r.u16())) return;
                break;
            case 0x04:
                if (!advance(r.u32())) return;
                break;
            case 0x05: // offset_extended
                state.rules.set(r.uleb(), { kind: "offset", offset: r.uleb() * cie.dataAlign });
                break;
            case 0x06: // restore_extended
                restore(r.uleb());
                break;
            case 0x07: // undefined
                state.rules.set(r.uleb(), { kind: "undefined" });
                break;
            case 0x08: // same_value
                state.rules.set(r.uleb(), { kind: "same" });
                break;
            case 0x09: // register
                state.rules.set(r.uleb(), { kind: "register", register: r.uleb() });
                break;
            case 0x0a: // remember_state
                saved.push({
                    rules: new Map(state.rules),
                    cfaRegister: state.cfaRegister,
                    cfaOffset: state.cfaOffset,
                });
                break;
            case 0x0b: {
                // restore_state
                const top = saved.pop();
                if (top !== undefined) {
                    state.rules = top.rules;
                    state.cfaRegister = top.cfaRegister;
                    state.cfaOffset = top.cfaOffset;
                }
                break;
            }
            case 0x0c: // def_cfa
                state.cfaRegister = r.uleb();
                state.cfaOffset = r.uleb();
                state.cfaKnown = true;
                break;
            case 0x0d: // def_cfa_register
                state.cfaRegister = r.uleb();
                break;
            case 0x0e: // def_cfa_offset
                state.cfaOffset = r.uleb();
                break;
            case 0x0f: // def_cfa_expression
                r.skip(r.uleb());
                state.cfaKnown = false;
                break;
            case 0x10: // expression
            case 0x16: // val_expression
                state.rules.set(r.uleb(), { kind: "unsupported" });
                r.skip(r.uleb());
                break;
            case 0x11: // offset_extended_sf
                state.rules.set(r.uleb(), { kind: "offset", offset: r.sleb() * cie.dataAlign });
                break;
            case 0x12: // def_cfa_sf
                state.cfaRegister = r.uleb();
                state.cfaOffset = r.sleb() * cie.dataAlign;
                state.cfaKnown = true;
                break;
            case 0x13: // def_cfa_offset_sf
                state.cfaOffset = r.sleb() * cie.dataAlign;
                break;
            case 0x14: // val_offset
                state.rules.set(r.uleb(), { kind: "valOffset", offset: r.uleb() * cie.dataAlign });
                break;
            case 0x15: // val_offset_sf
                state.rules.set(r.uleb(), { kind: "valOffset", offset: r.sleb() * cie.dataAlign });
                break;
            case 0x2e: // GNU_args_size
                r.uleb();
                break;
            case 0x2f: // GNU_negative_offset_extended
                state.rules.set(r.uleb(), { kind: "offset", offset: -r.uleb() * cie.dataAlign });
                break;
            default:
                return;
        }
    }
}
